/**
 * Sprint Redesign v1 · Phase 2+ · Baustein 3 (Do 03.09.2026, 16:45).
 *
 * /mein-muster — Fokustyp-Spiegel mit Tiefe.
 *
 * Zwei Rendering-States:
 *   1. Empty-State (kein Fokustyp gesetzt):
 *      Prominenter CTA „Mache jetzt deinen Fokustyp-Test" mit Erklärung,
 *      was danach hier sichtbar wird.
 *   2. Gefüllter State (Fokustyp gesetzt):
 *      Steckbrief-Header + 5 Perspektiven (Verhaltensweisen, Erwartungen,
 *      erste Impulse, Stressmuster, Werte) + Test-Wiederholen-Button.
 *
 * StressmusterGrid (Stufe 4D aus v1.6) bleibt in beiden States am Ende der Seite.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Eye, DoorOpen } from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import FokustypSpiegel from "@/components/redesign/FokustypSpiegel";
import { getFokustypTestRoute } from "@/lib/testRouting";

const VALID_SLUGS = new Set(["eule", "hund", "katze", "biene"]);

export default function PageMeinMuster() {
  const navigate = useNavigate();
  return (
    <RedesignLayout>
      {({ data }) => {
        const user = data?.user || {};
        const rawSlug = user.primary_fokustyp || user.fokustyp_primary || null;
        const slug = rawSlug && VALID_SLUGS.has(String(rawSlug).toLowerCase())
          ? String(rawSlug).toLowerCase()
          : null;
        const hasFokustyp = !!slug;

        return (
          <div
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8"
            data-testid="page-mein-muster"
            data-state={hasFokustyp ? "gefuellt" : "empty"}
          >
            <header>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                Phase 1
              </p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                So kommuniziere ich
              </h1>
              <p className="mt-2 text-slate-600 dark:text-slate-300 max-w-2xl">
                Wahrnehmung und Reaktion. Wie du tickst, worauf du achtest, wie du unter Druck reagierst — dein Muster im Klartext.
              </p>
            </header>

            {hasFokustyp ? (
              <FokustypSpiegel slug={slug} user={user} />
            ) : (
              <section
                className="rounded-2xl border-2 border-[var(--fts-primary-bright)] bg-white dark:bg-slate-900 p-6 sm:p-8 space-y-5"
                data-testid="mein-muster-empty-state"
              >
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                    Noch kein Fokustyp gesetzt
                  </p>
                  <h2
                    className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white"
                    data-testid="mein-muster-empty-headline"
                  >
                    Mache jetzt deinen Fokustyp-Test
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 max-w-xl">
                    Sobald dein Fokustyp gesetzt ist, siehst du hier deinen persönlichen Spiegel:
                  </p>
                </div>

                <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                  <li className="flex items-start gap-2">
                    <Compass className="w-4 h-4 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span><b>Verhaltensweisen</b> — wie du tickst im Alltag.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Eye className="w-4 h-4 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span><b>Erwartungshaltung</b> — was du von anderen erwartest.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <DoorOpen className="w-4 h-4 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span><b>Erste Impulse & Stressmuster</b> — wie du automatisch reagierst, gerade unter Druck.</span>
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() => navigate(getFokustypTestRoute(user))}
                  data-testid="mein-muster-empty-cta"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-4 py-2.5"
                >
                  Zeig mir meinen Fokustyp
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Dauert ca. 3 Minuten. Kostenlos. Dein Fokustyp bleibt gespeichert und ist die Basis für alle Phasen.
                </p>
              </section>
            )}
          </div>
        );
      }}
    </RedesignLayout>
  );
}
